import { type CSSProperties, type ReactNode } from 'react';

import LinkButton from '@atlaskit/button/link';

import Heading from '@atlaskit/heading/heading';

import AddIcon from '@atlaskit/icon/core/add';

import CheckCircleIcon from '@atlaskit/icon/core/check-circle';

import CommentAddIcon from '@atlaskit/icon/core/comment-add';

import EditIcon from '@atlaskit/icon/core/edit';

import PersonAddIcon from '@atlaskit/icon/core/person-add';

import { Box } from '@atlaskit/primitives/box';

import { Inline } from '@atlaskit/primitives/inline';

import { Stack } from '@atlaskit/primitives/stack';

import { Text } from '@atlaskit/primitives/text';

import { xcss } from '@atlaskit/primitives/xcss/xcss';

import { useIntl } from 'react-intl';

import { activationChecklistMessages, getActivationChecklistCopy } from '../../copy';

import { type ActivationChecklistActionType, type ActivationChecklistItemId } from '../../types';

export type ChecklistItemCardProps = {
	id: ActivationChecklistItemId;
	completed: boolean;
	actionType: ActivationChecklistActionType;
	cta?: {
		href: string;
	};
	hasTopBorder?: boolean;
	onClick?: () => void;
};

const itemBaseStyles = xcss({
	paddingBlock: 'space.200',
	paddingInline: 'space.200',
});

const itemStyles = xcss({
	backgroundColor: 'elevation.surface',
});

const completedItemStyles = xcss({
	backgroundColor: 'color.background.success.subtler',
	paddingInlineEnd: 'space.300',
});

const itemDividerStyles = xcss({
	borderBlockStartColor: 'color.border',
	borderBlockStartStyle: 'solid',
	borderBlockStartWidth: 'border.width',
});

const completedItemDividerStyles = xcss({
	borderBlockStartColor: 'color.background.success.subtler',
});

const itemDividerStyle = {
	borderBlockStartColor: 'var(--activation-checklist-border-color, var(--ds-border-bold, #172B4D))',
} satisfies CSSProperties;

const leadingIconStyles = xcss({
	flexShrink: 0,
	color: 'color.text.subtle',
	backgroundColor: 'color.background.neutral.subtle',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '32px',
	height: '32px',
});

const completedLeadingIconStyles = xcss({
	color: 'color.text.success',
	backgroundColor: 'color.background.success',
	borderColor: 'color.border.success',
});

const completedContentStyles = xcss({
	color: 'color.text.subtle',
});

const completedHeadingStyles = xcss({
	color: 'color.text.success',
});

const ActionIcon = ({ actionType }: { actionType: ActivationChecklistActionType }): ReactNode => {
	switch (actionType) {
		case 'createWorkItem':
			return <AddIcon label="" color="currentColor" />;
		case 'updateWorkItem':
			return <EditIcon label="" color="currentColor" />;
		case 'addComment':
			return <CommentAddIcon label="" color="currentColor" />;
		case 'inviteTeam':
			return <PersonAddIcon label="" color="currentColor" />;
		default: {
			const unexpectedActionType = actionType as string;
			const exhaustiveActionType: never = actionType;
			void exhaustiveActionType;
			throw new Error(`Unhandled activation checklist action type: ${unexpectedActionType}`);
		}
	}
};

export function ChecklistItemCard({
	id,
	completed,
	actionType,
	cta,
	hasTopBorder = false,
	onClick,
}: ChecklistItemCardProps): ReactNode {
	const intl = useIntl();
	const { title, description, ctaLabel } = getActivationChecklistCopy(intl, id);
	const completedLabel = intl.formatMessage(activationChecklistMessages.itemCompleted);

	return (
		<Box
			as="li"
			xcss={[
				itemBaseStyles,
				hasTopBorder ? itemDividerStyles : undefined,
				hasTopBorder && completed ? completedItemDividerStyles : undefined,
				completed ? completedItemStyles : itemStyles,
			]}
			style={hasTopBorder ? itemDividerStyle : undefined}
		>
			<Inline space="space.200" alignBlock="center" spread="space-between">
				<Inline space="space.150" alignBlock="start">
					<Box
						xcss={[leadingIconStyles, completed ? completedLeadingIconStyles : undefined]}
						aria-hidden={completed ? undefined : 'true'}
					>
						{completed ? (
							<CheckCircleIcon label={completedLabel} color="currentColor" />
						) : (
							<ActionIcon actionType={actionType} />
						)}
					</Box>
					<Stack space="space.050" xcss={completed ? completedContentStyles : undefined}>
						<Box xcss={completed ? completedHeadingStyles : undefined}>
							<Heading as="h3" size="xsmall">
								{title}
							</Heading>
						</Box>
						<Text color="color.text.subtle">{description}</Text>
					</Stack>
				</Inline>
				{cta && !completed ? (
					<LinkButton
						href={cta.href}
						onClick={onClick}
						appearance="default"
						aria-label={`${ctaLabel}: ${title}`}
					>
						{ctaLabel}
					</LinkButton>
				) : null}
			</Inline>
		</Box>
	);
}
